// Scope injection for kernels. A "scope-dependent" kernel is a recipe
// whose analytics queries should narrow to whatever scope the user is
// viewing on the activity surface (an endpoint, a cluster, the global
// overview, …) — re-parameterised at run time, not frozen to the scope
// it was authored under.
//
// The analytics Query proto already factors into two halves:
//
//   root + filter        → WHICH rows  (the scope owns this)
//   output + order + limit → WHAT to compute  (the kernel owns this)
//
// Dashboard scope is *defined* as a filtered set of prompts: every
// scope dim resolves to a predicate on the PROMPT entity (directly, or
// via the cluster join buildPromptRootAndFilter adds). So a
// kernel can absorb scope IFF its query is prompt-rooted — only then
// are its rows the same things the scope filters. We:
//
//   1. confirm the kernel's analytics_query is rooted at ENTITY_TYPE_PROMPT,
//   2. rebuild the scope's { root, filter } via buildPromptRootAndFilter
//      (canonical aliases p / ec, plus the joins the kernel's
//      group-by needs as extraDims),
//   3. REPLACE the kernel step's root with the scope root and AND the
//      scope filter into the kernel's own (intrinsic) filter, after
//      rebasing the kernel's field refs onto the canonical aliases.
//
// Kernel query specs use arbitrary aliases (the chat LLM authors them
// freely), so step 3 must remap — we can't assume the kernel rooted
// prompts under "p". Anything we can't safely canonicalize (a join to
// an entity other than TOKEN / ILO_EVENT_CLUSTER / SIGNAL_TAGS, an unsupported nested join,
// a root step_filter we'd drop, or a field ref to an unknown alias)
// returns a categorized { scoped: false, reason, reasonCode, reasonCategory }
// and the caller runs the query unscoped — a soft, logged fallback rather than
// a broken widget.

import { buildPromptRootAndFilter, dashboardScopeToApiScope } from "./analyticsClient.js";
import { ensureQueryJoins } from "./queryLangJoins.js";

const ENTITY_PROMPT = "ENTITY_TYPE_PROMPT";
const ENTITY_TOKEN = "ENTITY_TYPE_TOKEN";
const ENTITY_CLUSTER_ASSIGNMENT = "ENTITY_TYPE_ILO_EVENT_CLUSTER";
const ENTITY_CLUSTER_METADATA = "ENTITY_TYPE_ILO_CLUSTER";
const ENTITY_SIGNAL_TAGS = "ENTITY_TYPE_SIGNAL_TAGS";
const EDGE_PROMPT_CLUSTER_ASSIGNMENT = "EDGE_TYPE_PROMPT_HAS_EVENT_CLUSTER";
const EDGE_CLUSTER_ASSIGNMENT_METADATA = "EDGE_TYPE_EVENT_CLUSTER_IN_CLUSTER";
const EDGE_PROMPT_SIGNAL_TAGS = "EDGE_TYPE_PROMPT_HAS_SIGNAL_TAGS";
const SUPPORTED_PROMPT_HOP_MODE = "JOIN_MODE_INNER";

// Stable machine-readable taxonomy for scope-injection failures. Keep the
// human-readable `reason` too, but callers must branch on these values rather
// than parsing prose. Definition-shape failures are defects/limitations in the
// authored query and can be caught at publish time. Viewer-scope failures arise
// only from the active drilldown and therefore remain runtime-dependent.
export const KERNEL_SCOPE_FAILURE_CATEGORY = Object.freeze({
    DEFINITION_SHAPE: "definition-shape",
    VIEWER_SCOPE: "viewer-scope",
});

export const KERNEL_SCOPE_FAILURE_CODE = Object.freeze({
    MISSING_SPEC: "missing-spec",
    NOT_PROMPT_ROOTED: "not-prompt-rooted",
    ROOT_STEP_FILTER: "root-step-filter",
    UNSUPPORTED_VIEWER_SCOPE_DIMENSION: "unsupported-viewer-scope-dimension",
    PROMPT_TOKEN_JOIN: "prompt-token-join",
    UNSUPPORTED_JOIN_TARGET: "unsupported-join-target",
    NESTED_JOIN: "nested-join",
    ALIAS_COLLISION: "alias-collision",
    PROMPT_ROOT_SCOPE_GAP: "prompt-root-scope-gap",
    UNKNOWN_STEP_ALIAS: "unknown-step-alias",
});

const FAILURE_CATEGORY_BY_CODE = Object.freeze({
    [KERNEL_SCOPE_FAILURE_CODE.MISSING_SPEC]: KERNEL_SCOPE_FAILURE_CATEGORY.DEFINITION_SHAPE,
    [KERNEL_SCOPE_FAILURE_CODE.NOT_PROMPT_ROOTED]: KERNEL_SCOPE_FAILURE_CATEGORY.DEFINITION_SHAPE,
    [KERNEL_SCOPE_FAILURE_CODE.ROOT_STEP_FILTER]: KERNEL_SCOPE_FAILURE_CATEGORY.DEFINITION_SHAPE,
    [KERNEL_SCOPE_FAILURE_CODE.UNSUPPORTED_VIEWER_SCOPE_DIMENSION]: KERNEL_SCOPE_FAILURE_CATEGORY.VIEWER_SCOPE,
    [KERNEL_SCOPE_FAILURE_CODE.PROMPT_TOKEN_JOIN]: KERNEL_SCOPE_FAILURE_CATEGORY.DEFINITION_SHAPE,
    [KERNEL_SCOPE_FAILURE_CODE.UNSUPPORTED_JOIN_TARGET]: KERNEL_SCOPE_FAILURE_CATEGORY.DEFINITION_SHAPE,
    [KERNEL_SCOPE_FAILURE_CODE.NESTED_JOIN]: KERNEL_SCOPE_FAILURE_CATEGORY.DEFINITION_SHAPE,
    [KERNEL_SCOPE_FAILURE_CODE.ALIAS_COLLISION]: KERNEL_SCOPE_FAILURE_CATEGORY.DEFINITION_SHAPE,
    [KERNEL_SCOPE_FAILURE_CODE.PROMPT_ROOT_SCOPE_GAP]: KERNEL_SCOPE_FAILURE_CATEGORY.VIEWER_SCOPE,
    [KERNEL_SCOPE_FAILURE_CODE.UNKNOWN_STEP_ALIAS]: KERNEL_SCOPE_FAILURE_CATEGORY.DEFINITION_SHAPE,
});

// Canonical aliases buildPromptRootAndFilter emits. The kernel's own
// aliases get remapped onto these so their field refs resolve against the
// scope-supplied root.
const ALIAS_PROMPT = "p";
const ALIAS_CLUSTER_ASSIGNMENT = "ec";
const ALIAS_CLUSTER_METADATA = "c";
const ALIAS_SIGNAL_TAGS = "st";

// Trigger dim that makes buildPromptRootAndFilter add the cluster join (it keys
// off DIM_INFO[d].viaCluster). There is no token trigger:
// buildPromptRootAndFilter never attaches a TOKEN child, so a token-join
// kernel fails closed below.
const CLUSTER_TRIGGER_DIM = "cluster";
const SIGNAL_TRIGGER_DIM = "signal";

// Inject the dashboard scope into a single analytics_query spec.
// Returns { spec, scoped, reason, reasonCode, reasonCategory }:
//   • scoped:true  → `spec` is the scope-narrowed query, run this.
//   • scoped:false → `spec` is the input unchanged; `reason` explains
//     why injection was skipped, while reasonCode/reasonCategory are the
//     stable contract for caller behavior (today: run unscoped + warn).
// `query` (optional) is the compiled query-language refinement — a PROMPT-rooted
// FilterExpr + its cross-entity joins ({ filter, joins }). It's just more scope:
// the joins ride the rebuilt scope root's extraDims, the filter ANDs onto the
// scope filter. null/empty → unchanged behavior.
export function applyScopeToSpec(spec, scopePath, query = null) {
    if (!spec || typeof spec !== "object") {
        return scopeFailure(spec, KERNEL_SCOPE_FAILURE_CODE.MISSING_SPEC, "no spec");
    }
    const root = spec.root;
    if (!root || root.entity !== ENTITY_PROMPT) {
        return scopeFailure(
            spec,
            KERNEL_SCOPE_FAILURE_CODE.NOT_PROMPT_ROOTED,
            `not prompt-rooted (root entity: ${root?.entity ?? "none"})`,
        );
    }
    // A root step_filter (ON-clause predicates on the prompt root) would
    // be silently dropped when we swap the root. Bail rather than lose it.
    if (Array.isArray(root.stepFilter) && root.stepFilter.length) {
        return scopeFailure(
            spec,
            KERNEL_SCOPE_FAILURE_CODE.ROOT_STEP_FILTER,
            "root step_filter would be lost",
        );
    }

    // Dashboard scope must be fully expressible against the prompt root.
    // A path that contains a dim with no prompt-rooted form (identities,
    // files, …) would produce a partial filter that LOOKS scoped but
    // isn't — refuse rather than mislead.
    const { apiScope, unsupported } = dashboardScopeToApiScope(scopePath);
    if (unsupported.length) {
        return scopeFailure(
            spec,
            KERNEL_SCOPE_FAILURE_CODE.UNSUPPORTED_VIEWER_SCOPE_DIMENSION,
            `scope dims unsupported by analytics: ${unsupported.join(", ")}`,
        );
    }

    // Map the kernel's aliases → canonical, and learn which joins it uses
    // so the rebuilt scope root carries them.
    const aliasMap = Object.create(null);
    const authoredAliases = new Set();
    const registerAlias = (authoredAlias, canonicalAlias) => {
        if (typeof authoredAlias !== "string"
            || authoredAlias.length === 0
            || authoredAliases.has(authoredAlias)) {
            return false;
        }
        authoredAliases.add(authoredAlias);
        aliasMap[authoredAlias] = canonicalAlias;
        return true;
    };
    if (!registerAlias(root.alias, ALIAS_PROMPT)) {
        return scopeFailure(
            spec,
            KERNEL_SCOPE_FAILURE_CODE.ALIAS_COLLISION,
            "query step aliases must be non-empty and unique",
        );
    }
    let needCluster = false;
    let needClusterMetadata = false;
    let needSignalTags = false;
    let clusterMetadataMode;
    let clusterAssignmentStepFilter;
    let clusterMetadataStepFilter;
    let signalTagsStepFilter;
    const children = Array.isArray(root.children) ? root.children : [];
    for (const child of children) {
        const target = child?.target;
        const ent = target?.entity;
        if (ent === ENTITY_TOKEN) {
            // buildPromptRootAndFilter never attaches a TOKEN child, so it
            // can't reconstruct a kernel's prompt→token join (even though
            // EDGE_TYPE_PROMPT_HAS_TOKENS exists, O-2021) — rebuilding the
            // root would leave the kernel's token-step output refs dangling
            // and the planner would reject the unknown alias. Fail closed
            // (run unscoped) rather than emit a broken scoped query;
            // per-model/token surfaces reach TOKEN by rooting at it or via
            // the PROMPT_HAS_TOKENS collapse, not as a PROMPT child.
            return scopeFailure(
                spec,
                KERNEL_SCOPE_FAILURE_CODE.PROMPT_TOKEN_JOIN,
                "kernel joins PROMPT→TOKEN; scope injection can't reconstruct the token join",
            );
        }
        else if (ent === ENTITY_CLUSTER_ASSIGNMENT) {
            if (child.edge !== EDGE_PROMPT_CLUSTER_ASSIGNMENT
                || child.mode !== SUPPORTED_PROMPT_HOP_MODE) {
                return scopeFailure(
                    spec,
                    KERNEL_SCOPE_FAILURE_CODE.UNSUPPORTED_JOIN_TARGET,
                    `unsupported PROMPT to ILO_EVENT_CLUSTER hop: ${child.edge ?? "unknown"} / ${child.mode ?? "unspecified"}`,
                );
            }
            if (needCluster) {
                return scopeFailure(
                    spec,
                    KERNEL_SCOPE_FAILURE_CODE.NESTED_JOIN,
                    "multiple PROMPT to ILO_EVENT_CLUSTER joins are not supported",
                );
            }
            if (!registerAlias(target.alias, ALIAS_CLUSTER_ASSIGNMENT)) {
                return scopeFailure(
                    spec,
                    KERNEL_SCOPE_FAILURE_CODE.ALIAS_COLLISION,
                    "query step aliases must be non-empty and unique",
                );
            }
            if (target.stepFilter !== undefined && !Array.isArray(target.stepFilter)) {
                return scopeFailure(
                    spec,
                    KERNEL_SCOPE_FAILURE_CODE.UNSUPPORTED_JOIN_TARGET,
                    "ILO_EVENT_CLUSTER step_filter must be an array to preserve it safely",
                );
            }
            needCluster = true;
            clusterAssignmentStepFilter = target.stepFilter;
            const nested = target.children ?? [];
            if (nested.length > 1 || (nested.length === 1 && (
                nested[0]?.edge !== EDGE_CLUSTER_ASSIGNMENT_METADATA
                || nested[0]?.target?.entity !== ENTITY_CLUSTER_METADATA
                || (nested[0]?.target?.children?.length ?? 0) > 0
            ))) {
                return scopeFailure(
                    spec,
                    KERNEL_SCOPE_FAILURE_CODE.NESTED_JOIN,
                    "nested joins beyond the cluster metadata hop are not supported",
                );
            }
            if (nested.length === 1) {
                if (!registerAlias(nested[0].target.alias, ALIAS_CLUSTER_METADATA)) {
                    return scopeFailure(
                        spec,
                        KERNEL_SCOPE_FAILURE_CODE.ALIAS_COLLISION,
                        "query step aliases must be non-empty and unique",
                    );
                }
                if (nested[0].target.stepFilter !== undefined
                    && !Array.isArray(nested[0].target.stepFilter)) {
                    return scopeFailure(
                        spec,
                        KERNEL_SCOPE_FAILURE_CODE.UNSUPPORTED_JOIN_TARGET,
                        "ILO_CLUSTER step_filter must be an array to preserve it safely",
                    );
                }
                needClusterMetadata = true;
                clusterMetadataMode = nested[0].mode;
                clusterMetadataStepFilter = nested[0].target.stepFilter;
            }
        }
        else if (ent === ENTITY_SIGNAL_TAGS) {
            if (child.edge !== EDGE_PROMPT_SIGNAL_TAGS
                || child.mode !== SUPPORTED_PROMPT_HOP_MODE) {
                return scopeFailure(
                    spec,
                    KERNEL_SCOPE_FAILURE_CODE.UNSUPPORTED_JOIN_TARGET,
                    `unsupported PROMPT to SIGNAL_TAGS hop: ${child.edge ?? "unknown"} / ${child.mode ?? "unspecified"}`,
                );
            }
            if (needSignalTags) {
                return scopeFailure(
                    spec,
                    KERNEL_SCOPE_FAILURE_CODE.NESTED_JOIN,
                    "multiple PROMPT to SIGNAL_TAGS joins are not supported",
                );
            }
            if (!registerAlias(target.alias, ALIAS_SIGNAL_TAGS)) {
                return scopeFailure(
                    spec,
                    KERNEL_SCOPE_FAILURE_CODE.ALIAS_COLLISION,
                    "query step aliases must be non-empty and unique",
                );
            }
            if (target.stepFilter !== undefined && !Array.isArray(target.stepFilter)) {
                return scopeFailure(
                    spec,
                    KERNEL_SCOPE_FAILURE_CODE.UNSUPPORTED_JOIN_TARGET,
                    "SIGNAL_TAGS step_filter must be an array to preserve it safely",
                );
            }
            needSignalTags = true;
            signalTagsStepFilter = target.stepFilter;
            if (target.children?.length) {
                return scopeFailure(
                    spec,
                    KERNEL_SCOPE_FAILURE_CODE.NESTED_JOIN,
                    "nested joins below SIGNAL_TAGS are not supported",
                );
            }
        }
        else {
            return scopeFailure(
                spec,
                KERNEL_SCOPE_FAILURE_CODE.UNSUPPORTED_JOIN_TARGET,
                `unsupported join target for scope injection: ${ent ?? "unknown"}`,
            );
        }
    }

    const extraDims = [];
    if (needCluster) extraDims.push(CLUSTER_TRIGGER_DIM);
    if (needSignalTags) extraDims.push(SIGNAL_TRIGGER_DIM);

    const { root: scopeRoot, filter: scopeFilter, unsupported: scopeUnsupported } = buildPromptRootAndFilter({
        scopePath: apiScope,
        extraDims,
    });
    // A scope dim the PROMPT root can't carry (today: `model`, viaUsage — O-1805)
    // is silently dropped by buildPromptRootAndFilter. dashboardScopeToApiScope
    // above only catches dims with NO prompt-rooted form (files/identities), not
    // `model` (a supported dim the PROMPT root just can't hold), so guard here too
    // — otherwise the kernel runs scoped-but-widened across all models. Fail closed
    // (run unscoped + log) rather than mislead.
    if (scopeUnsupported.length) {
        return scopeFailure(
            spec,
            KERNEL_SCOPE_FAILURE_CODE.PROMPT_ROOT_SCOPE_GAP,
            `scope dims unsupported by the PROMPT root (dropped): ${scopeUnsupported.join(", ")}`,
        );
    }

    if (needClusterMetadata) {
        const clusterStep = scopeRoot.children?.find(
            (child) => child.target?.alias === ALIAS_CLUSTER_ASSIGNMENT,
        )?.target;
        if (!clusterStep) {
            return scopeFailure(
                spec,
                KERNEL_SCOPE_FAILURE_CODE.NESTED_JOIN,
                "cluster metadata join requires the canonical event-cluster step",
            );
        }
        const metadataJoin = {
            edge: EDGE_CLUSTER_ASSIGNMENT_METADATA,
            target: { alias: ALIAS_CLUSTER_METADATA, entity: ENTITY_CLUSTER_METADATA },
        };
        if (clusterMetadataMode !== undefined) metadataJoin.mode = clusterMetadataMode;
        clusterStep.children = [metadataJoin];
    }

    // Rebase the kernel's field refs onto the canonical aliases. The
    // kernel owns the SHAPE-half (output / order_by / limit / sample) but
    // authored it against its own root alias; every `stepAlias` it
    // carries must map onto a canonical alias. We walk the whole spec (minus the
    // root we're replacing, and minus the filter, which is AND-ed in
    // separately) and remap every stepAlias generically — a stepAlias
    // only ever appears inside a FieldRef, so one recursive pass covers
    // dimensions, computed AND standard metrics, sample/partition refs,
    // and every FilterExpr variant (and/or/not/pred) without enumerating
    // shapes. An alias absent from the map means the kernel reaches
    // outside its own root/joins → bail (fail closed).
    let aliasMiss = false;
    const onMiss = () => { aliasMiss = true; };
    const { root: _kernelRoot, filter: kernelFilter, ...kernelShape } = spec;
    const rebasedShape = rebaseAliases(kernelShape, aliasMap, onMiss);
    const rebasedFilter = kernelFilter !== undefined ? rebaseAliases(kernelFilter, aliasMap, onMiss) : undefined;
    const rebasedClusterAssignmentStepFilter = clusterAssignmentStepFilter !== undefined
        ? rebaseAliases(clusterAssignmentStepFilter, aliasMap, onMiss)
        : undefined;
    const rebasedClusterMetadataStepFilter = clusterMetadataStepFilter !== undefined
        ? rebaseAliases(clusterMetadataStepFilter, aliasMap, onMiss)
        : undefined;
    const rebasedSignalTagsStepFilter = signalTagsStepFilter !== undefined
        ? rebaseAliases(signalTagsStepFilter, aliasMap, onMiss)
        : undefined;
    if (aliasMiss) {
        return scopeFailure(
            spec,
            KERNEL_SCOPE_FAILURE_CODE.UNKNOWN_STEP_ALIAS,
            "kernel references an alias outside its root/joins",
        );
    }

    // Query-language cluster/identity joins attach LEFT (via ensureQueryJoins) —
    // matching every other surface (getTraces / overview / bundle) — so an
    // `agent:x OR identity:y` branch keeps prompts with no assignment. The
    // kernel's OWN scope joins above stay INNER (via extraDims): scope means
    // "drop rows that don't match", a query OR must not.
    const queryJoins = query?.joins || [];
    const clusterStep = scopeRoot.children?.find(
        (child) => child.target?.alias === ALIAS_CLUSTER_ASSIGNMENT,
    )?.target;
    if (rebasedClusterAssignmentStepFilter !== undefined) {
        clusterStep.stepFilter = [
            ...(clusterStep.stepFilter ?? []),
            ...rebasedClusterAssignmentStepFilter,
        ];
    }
    if (rebasedClusterMetadataStepFilter !== undefined) {
        const metadataStep = clusterStep?.children?.find(
            (child) => child.target?.alias === ALIAS_CLUSTER_METADATA,
        )?.target;
        metadataStep.stepFilter = [...rebasedClusterMetadataStepFilter];
    }
    if (rebasedSignalTagsStepFilter !== undefined) {
        const signalTagsStep = scopeRoot.children?.find(
            (child) => child.target?.alias === ALIAS_SIGNAL_TAGS,
        )?.target;
        signalTagsStep.stepFilter = [
            ...(signalTagsStep.stepFilter ?? []),
            ...rebasedSignalTagsStepFilter,
        ];
    }
    const scopeRootJoined = queryJoins.length ? ensureQueryJoins(scopeRoot, queryJoins) : scopeRoot;

    const next = { ...rebasedShape, root: scopeRootJoined };
    let filter = andCompose(scopeFilter, rebasedFilter);
    // The query refinement is more PROMPT-rooted scope — AND it on too.
    if (query?.filter) filter = filter === undefined ? query.filter : andCompose(filter, query.filter);
    if (filter !== undefined) next.filter = filter;
    return { spec: next, scoped: true };
}

function scopeFailure(spec, reasonCode, reason) {
    return {
        spec,
        scoped: false,
        reason,
        reasonCode,
        reasonCategory: FAILURE_CATEGORY_BY_CODE[reasonCode],
    };
}

// Recursively remap every `stepAlias` in a query fragment onto its
// canonical alias. `stepAlias` only ever appears inside a FieldRef
// ({ stepAlias, field }), so a generic walk safely covers dimensions,
// computed + standard metrics, sample/partition refs, and every
// FilterExpr variant (and/or/not/pred) — an unknown shape is recursed
// into rather than passed through untouched. An alias absent from the
// map fires onMiss so the caller can fail closed. Immutable: returns
// fresh objects/arrays, leaving the input spec untouched.
function rebaseAliases(node, aliasMap, onMiss) {
    if (Array.isArray(node)) return node.map((n) => rebaseAliases(n, aliasMap, onMiss));
    if (node && typeof node === "object") {
        const out = {};
        for (const [k, v] of Object.entries(node)) {
            if (k === "stepAlias" && typeof v === "string") {
                const mapped = aliasMap[v];
                if (mapped === undefined) { onMiss(); out[k] = v; }
                else out[k] = mapped;
            } else {
                out[k] = rebaseAliases(v, aliasMap, onMiss);
            }
        }
        return out;
    }
    return node;
}

// AND two FilterExprs, dropping nulls. Single survivor returns as-is;
// none returns undefined (→ no top-level filter).
function andCompose(a, b) {
    const parts = [a, b].filter((x) => x != null);
    if (parts.length === 0) return undefined;
    if (parts.length === 1) return parts[0];
    return { andExpr: { children: parts } };
}
